import { useState } from "react";
import { MapPin, Search, ExternalLink } from "lucide-react";

const POSTCODE_RANGES = [
  { min: 2000, max: 2599, state: "NSW", name: "New South Wales" },
  { min: 2619, max: 2899, state: "NSW", name: "New South Wales" },
  { min: 2921, max: 2999, state: "NSW", name: "New South Wales" },
  { min: 200, max: 299, state: "ACT", name: "Australian Capital Territory" },
  { min: 2600, max: 2618, state: "ACT", name: "Australian Capital Territory" },
  { min: 2900, max: 2920, state: "ACT", name: "Australian Capital Territory" },
  { min: 3000, max: 3999, state: "VIC", name: "Victoria" },
  { min: 4000, max: 4999, state: "QLD", name: "Queensland" },
  { min: 5000, max: 5999, state: "SA", name: "South Australia" },
  { min: 6000, max: 6999, state: "WA", name: "Western Australia" },
  { min: 7000, max: 7999, state: "TAS", name: "Tasmania" },
  { min: 800, max: 999, state: "NT", name: "Northern Territory" },
  { min: 1000, max: 1999, state: "NSW", name: "New South Wales (LPO)" },
];

const STATE_COMMISSIONS = {
  NSW: { label: "NSW Electoral Commission", url: (pc) => `https://www.elections.nsw.gov.au/?postcode=${pc}` },
  VIC: { label: "Victorian Electoral Commission", url: (pc) => `https://myelectorate.vec.vic.gov.au/findmyelectorate?address=${pc}` },
  QLD: { label: "Electoral Commission QLD", url: (pc) => `https://www.ecq.qld.gov.au/elections/find-my-electorate?address=${pc}` },
  SA: { label: "Electoral Commission SA", url: (pc) => `https://www.ecsa.sa.gov.au/enrolment-and-voting/voter-information/find-your-electorate?postcode=${pc}` },
  WA: { label: "WA Electoral Commission", url: (pc) => `https://www.elections.wa.gov.au/find-a-candidate-or-polling-place?postcode=${pc}` },
  TAS: { label: "Tasmanian Electoral Commission", url: (pc) => `https://www.tec.tas.gov.au` },
  ACT: { label: "ACT Electoral Commission", url: (pc) => `https://www.elections.act.gov.au` },
  NT: { label: "NT Electoral Commission", url: (pc) => `https://ntec.nt.gov.au` },
};

function getStateFromPostcode(postcode) {
  const num = parseInt(postcode, 10);
  for (const r of POSTCODE_RANGES) {
    if (num >= r.min && num <= r.max) return r;
  }
  return null;
}

export default function ElectorateFinder() {
  const [postcode, setPostcode] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleSearch = () => {
    const clean = postcode.trim();
    if (!/^\d{3,4}$/.test(clean)) {
      setError("Please enter a valid 3–4 digit Australian postcode.");
      setResult(null);
      return;
    }
    const found = getStateFromPostcode(clean);
    if (!found) {
      setError("Postcode not recognised. Please check and try again.");
      setResult(null);
      return;
    }
    setError("");
    setResult(found);
  };

  return (
    <div
      className="bg-white border-2 border-black p-6"
      style={{ boxShadow: "4px 4px 0px 0px #000000" }}
      data-testid="electorate-finder"
    >
      <div className="flex items-center gap-2 mb-3">
        <MapPin className="w-5 h-5" style={{ color: "#2E5948" }} />
        <h3
          className="font-black text-lg"
          style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}
        >
          Find Your Electorate
        </h3>
      </div>
      <p className="text-sm text-[#475569] mb-4">
        Enter your postcode to find your federal electorate and state electoral commission.
      </p>

      <div className="flex gap-2 mb-3">
        <input
          type="text"
          inputMode="numeric"
          placeholder="e.g. 3000"
          value={postcode}
          data-testid="postcode-input"
          onChange={(e) => setPostcode(e.target.value.replace(/\D/g, "").slice(0, 4))}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          className="flex-1 border-2 border-black px-3 py-2.5 text-sm font-medium focus:outline-none focus:border-[#2E5948] bg-white"
          maxLength={4}
        />
        <button
          data-testid="find-electorate-btn"
          onClick={handleSearch}
          className="px-4 py-2.5 text-white font-bold text-sm border-2 border-black hover:-translate-y-0.5 transition-transform"
          style={{ backgroundColor: "#2E5948", boxShadow: "2px 2px 0px 0px #000" }}
        >
          <Search className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <p className="text-xs font-medium mb-2" style={{ color: "#C04E3F" }} data-testid="postcode-error">
          {error}
        </p>
      )}

      {result && (
        <div
          className="mt-3 p-4 border-2 border-black"
          style={{ backgroundColor: "#F9F7F2" }}
          data-testid="electorate-result"
        >
          <p className="text-sm font-black text-[#0F172A] mb-1">
            Postcode {postcode} — {result.name}
          </p>
          <p className="text-xs text-[#475569] mb-3">
            Your postcode: <strong>{postcode}</strong> · State: {result.state}
          </p>
          <div className="flex flex-col gap-2">
            <a
              href={`https://electorate.aec.gov.au/?address=${encodeURIComponent(postcode)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="aec-electorate-link"
              className="flex items-center gap-2 text-xs font-bold text-white px-3 py-2.5 border border-black hover:opacity-90 transition-opacity"
              style={{ backgroundColor: "#2E5948" }}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Find My Federal Electorate (AEC) — postcode pre-filled
            </a>
            {STATE_COMMISSIONS[result.state] && (
              <a
                href={STATE_COMMISSIONS[result.state].url(postcode)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="state-commission-link"
                className="flex items-center gap-2 text-xs font-bold px-3 py-2.5 border border-black hover:opacity-90 transition-opacity"
                style={{ backgroundColor: "#FFC82E", color: "#000" }}
              >
                <ExternalLink className="w-3.5 h-3.5" />
                {STATE_COMMISSIONS[result.state].label} — postcode pre-filled
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
